import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { invalidateInstitutional } from "../../lib/brand";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Building2, Phone, MapPin, Share2, KeyRound, Save } from "lucide-react";
import { getUser } from "../../lib/auth";

const EMPTY = {
  legal_name: "",
  trade_name: "",
  cnpj: "",
  phone: "",
  whatsapp: "",
  email_institutional: "",
  email_reservations: "",
  business_hours: "",
  address: { street: "", number: "", complement: "", neighborhood: "", city: "", state: "", postal_code: "" },
  social: { instagram: "", facebook: "", linkedin: "", youtube: "" },
  pix: { kind: "", value: "" },
};

function Section({ icon: Icon, title, description, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md p-6 mb-6">
      <div className="flex items-start gap-3 mb-4">
        <Icon className="w-5 h-5 text-[#0AA89C] mt-0.5" />
        <div>
          <h2 className="font-semibold text-[#22302E]">{title}</h2>
          {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

export default function AdminInstitutional() {
  const user = getUser();
  const isDiretor = user?.role === "diretor";
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/institutional");
      // Ensure nested objects always defined
      const merged = { ...EMPTY, ...r.data,
        address: { ...EMPTY.address, ...(r.data.address || {}) },
        social: { ...EMPTY.social, ...(r.data.social || {}) },
        pix: { ...EMPTY.pix, ...(r.data.pix || {}) },
      };
      setF(merged);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    if (!isDiretor) return;
    setSaving(true);
    try {
      // Send whatsapp/phone digits only (backend accepts any but we normalize)
      const payload = {
        ...f,
        whatsapp: (f.whatsapp || "").replace(/\D/g, ""),
        phone: (f.phone || "").replace(/\D/g, ""),
        cnpj: (f.cnpj || "").replace(/\D/g, ""),
      };
      await api.put("/admin/institutional", payload);
      invalidateInstitutional();
      toast.success("Configurações institucionais salvas");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-sm text-slate-500">Carregando…</div>;

  const set = (path, val) => {
    setF((prev) => {
      const copy = { ...prev };
      if (path.includes(".")) {
        const [a, b] = path.split(".");
        copy[a] = { ...copy[a], [b]: val };
      } else {
        copy[path] = val;
      }
      return copy;
    });
  };

  const readOnly = !isDiretor;

  return (
    <div className="max-w-4xl" data-testid="admin-institutional-page">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <span className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Empresa</span>
          <h1 className="text-3xl font-semibold text-[#0AA89C] mt-1">Configurações institucionais</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            Dados oficiais da Azuhomes. As informações públicas (razão social, CNPJ, telefone,
            e-mails, endereço, redes sociais e horário de atendimento) aparecem automaticamente
            no rodapé do site, na página de contato, nas políticas e nos e-mails enviados.
            A chave Pix permanece estritamente interna.
          </p>
        </div>
        {isDiretor && (
          <Button onClick={save} disabled={saving} className="bg-[#0AA89C] hover:bg-[#066B63]" data-testid="save-institutional">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Salvando…" : "Salvar"}
          </Button>
        )}
      </div>

      {!isDiretor && (
        <div className="mb-6 p-4 rounded-md border border-amber-200 bg-amber-50 text-sm text-amber-900">
          Apenas o Diretor pode editar essas informações. Os demais perfis podem consultar.
        </div>
      )}

      <Section icon={Building2} title="Identificação legal" description="Aparece no rodapé do site e em documentos oficiais.">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label htmlFor="legal_name">Razão social</Label>
            <Input id="legal_name" value={f.legal_name || ""} disabled={readOnly} onChange={(e) => set("legal_name", e.target.value)} data-testid="inst-legal-name" />
          </div>
          <div>
            <Label htmlFor="trade_name">Nome fantasia</Label>
            <Input id="trade_name" value={f.trade_name || ""} disabled={readOnly} onChange={(e) => set("trade_name", e.target.value)} data-testid="inst-trade-name" />
          </div>
          <div>
            <Label htmlFor="cnpj">CNPJ</Label>
            <Input id="cnpj" value={f.cnpj || ""} disabled={readOnly} onChange={(e) => set("cnpj", e.target.value)} placeholder="00.000.000/0000-00" data-testid="inst-cnpj" />
          </div>
        </div>
      </Section>

      <Section icon={Phone} title="Contato" description="Telefones, e-mails e horário de atendimento exibidos publicamente.">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="phone">Telefone fixo</Label>
            <Input id="phone" value={f.phone || ""} disabled={readOnly} onChange={(e) => set("phone", e.target.value)} placeholder="(00) 0000-0000" data-testid="inst-phone" />
          </div>
          <div>
            <Label htmlFor="whatsapp">WhatsApp</Label>
            <Input id="whatsapp" value={f.whatsapp || ""} disabled={readOnly} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+55 (00) 00000-0000" data-testid="inst-whatsapp" />
          </div>
          <div>
            <Label htmlFor="email_institutional">E-mail institucional</Label>
            <Input id="email_institutional" type="email" value={f.email_institutional || ""} disabled={readOnly} onChange={(e) => set("email_institutional", e.target.value)} data-testid="inst-email-institutional" />
          </div>
          <div>
            <Label htmlFor="email_reservations">E-mail de reservas</Label>
            <Input id="email_reservations" type="email" value={f.email_reservations || ""} disabled={readOnly} onChange={(e) => set("email_reservations", e.target.value)} data-testid="inst-email-reservations" />
          </div>
          <div className="col-span-2">
            <Label htmlFor="business_hours">Horário de atendimento</Label>
            <Input id="business_hours" value={f.business_hours || ""} disabled={readOnly} onChange={(e) => set("business_hours", e.target.value)} placeholder="Seg. a sex., 9h às 18h" data-testid="inst-business-hours" />
          </div>
        </div>
      </Section>

      <Section icon={MapPin} title="Endereço" description="Endereço da sede — exibido no rodapé conforme LGPD e regras de e-commerce.">
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-4"><Label>Logradouro</Label><Input value={f.address.street || ""} disabled={readOnly} onChange={(e) => set("address.street", e.target.value)} data-testid="inst-addr-street" /></div>
          <div className="col-span-2"><Label>Número</Label><Input value={f.address.number || ""} disabled={readOnly} onChange={(e) => set("address.number", e.target.value)} data-testid="inst-addr-number" /></div>
          <div className="col-span-3"><Label>Complemento</Label><Input value={f.address.complement || ""} disabled={readOnly} onChange={(e) => set("address.complement", e.target.value)} /></div>
          <div className="col-span-3"><Label>Bairro</Label><Input value={f.address.neighborhood || ""} disabled={readOnly} onChange={(e) => set("address.neighborhood", e.target.value)} data-testid="inst-addr-neighborhood" /></div>
          <div className="col-span-3"><Label>Cidade</Label><Input value={f.address.city || ""} disabled={readOnly} onChange={(e) => set("address.city", e.target.value)} data-testid="inst-addr-city" /></div>
          <div className="col-span-1"><Label>UF</Label><Input value={f.address.state || ""} maxLength={2} disabled={readOnly} onChange={(e) => set("address.state", e.target.value.toUpperCase())} /></div>
          <div className="col-span-2"><Label>CEP</Label><Input value={f.address.postal_code || ""} disabled={readOnly} onChange={(e) => set("address.postal_code", e.target.value)} placeholder="00000-000" data-testid="inst-addr-postal" /></div>
        </div>
      </Section>

      <Section icon={Share2} title="Redes sociais" description="Perfis oficiais linkados no rodapé.">
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Instagram</Label><Input value={f.social.instagram || ""} disabled={readOnly} onChange={(e) => set("social.instagram", e.target.value)} placeholder="https://instagram.com/azuhomes" /></div>
          <div><Label>Facebook</Label><Input value={f.social.facebook || ""} disabled={readOnly} onChange={(e) => set("social.facebook", e.target.value)} /></div>
          <div><Label>LinkedIn</Label><Input value={f.social.linkedin || ""} disabled={readOnly} onChange={(e) => set("social.linkedin", e.target.value)} /></div>
          <div><Label>YouTube</Label><Input value={f.social.youtube || ""} disabled={readOnly} onChange={(e) => set("social.youtube", e.target.value)} /></div>
        </div>
      </Section>

      <Section icon={KeyRound} title="Chave Pix (uso interno)" description="Nunca exibida publicamente. Utilizada apenas em referências operacionais. Cobranças oficiais aos hóspedes seguem via Mercado Pago.">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Tipo de chave</Label>
            <Select value={f.pix.kind || ""} onValueChange={(v) => set("pix.kind", v)} disabled={readOnly}>
              <SelectTrigger data-testid="inst-pix-kind"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="cpf">CPF</SelectItem>
                <SelectItem value="cnpj">CNPJ</SelectItem>
                <SelectItem value="email">E-mail</SelectItem>
                <SelectItem value="phone">Telefone</SelectItem>
                <SelectItem value="random">Chave aleatória</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Valor da chave</Label>
            <Input value={f.pix.value || ""} disabled={readOnly} onChange={(e) => set("pix.value", e.target.value)} data-testid="inst-pix-value" autoComplete="off" />
          </div>
        </div>
      </Section>

      {isDiretor && (
        <div className="sticky bottom-4 mt-8 flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-[#0AA89C] hover:bg-[#066B63]" data-testid="save-institutional-bottom">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Salvando…" : "Salvar alterações"}
          </Button>
        </div>
      )}
    </div>
  );
}
